export const HEXCHAIN_ROW_COUNTS = [4, 5, 6, 7, 6, 5, 4] as const;
export const HEXCHAIN_TILE_COUNT = HEXCHAIN_ROW_COUNTS.reduce((sum, count) => sum + count, 0);
export const HEXCHAIN_MATCH_MS = 90_000;
export const HEXCHAIN_FREEZE_AGE = 7;

export type HexchainTile = {
  color: number;
  age: number;
  frozen: boolean;
  special?: "wild";
};

export type HexchainPlayer = {
  id: string;
  name: string;
  board: HexchainTile[];
  score: number;
  chains: number;
  bestChain: number;
  fiveStreak: number;
  incomingFrost: number;
  shuffleLeft: boolean;
  alive: boolean;
  stuck: boolean;
  movesSinceWildcard: number;
  nextWildcardAt: number;
  rngState: number;
};

export type HexchainEvent = {
  id: number;
  kind: "chain" | "shuffle" | "stuck" | "eliminated" | "finished";
  playerId: string;
  targetPlayerId?: string;
  chainLength?: number;
  gained?: number;
  baseGained?: number;
  wipeBonus?: number;
  previousBestChain?: number;
  attack?: number;
  cancelled?: number;
  landed?: number;
  path?: number[];
  animation?: HexchainAnimation;
  message: string;
};

export type HexchainBoardTransition = {
  board: HexchainTile[];
  clearedIndexes: number[];
  moved: Array<{ from: number; to: number }>;
  spawnedIndexes: number[];
};

export type HexchainAnimation = {
  before: HexchainTile[];
  primary: HexchainBoardTransition;
  wipe?: HexchainBoardTransition & { highlightedIndexes: number[] };
  frost?: { before: HexchainTile[]; after: HexchainTile[]; landedIndexes: number[] };
  chainColor?: number;
  resetKind?: "color" | "all" | "shuffle";
  resetIndexes: number[];
};

export type HexchainState = {
  status: "lobby" | "battle" | "finished";
  durationMs: number;
  startedAt: number | null;
  deadline: number | null;
  seed: number;
  players: Record<string, HexchainPlayer>;
  playerOrder: string[];
  winnerIds: string[];
  lastEvent: HexchainEvent | null;
  eventSequence: number;
};

export type HexchainPublicState = Omit<HexchainState, "players"> & {
  players: Record<string, Omit<HexchainPlayer, "rngState">>;
};

export type HexchainRoomPlayer = { id: string; name: string; connected: boolean };

const POINTS: Record<number, number> = { 3: 150, 4: 400, 5: 850, 6: 1_500, 7: 2_500, 8: 4_000, 9: 6_500, 10: 10_000 };

const cellCoordinates = (() => {
  const cells: Array<{ x: number; y: number }> = [];
  HEXCHAIN_ROW_COUNTS.forEach((count, row) => {
    const startX = -((count - 1) * 96) / 2;
    for (let column = 0; column < count; column += 1) {
      cells.push({ x: startX + column * 96, y: (row - 3) * 83 });
    }
  });
  return cells;
})();

const axialCoordinates = (() => {
  const coordinates: Array<{ q: number; r: number }> = [];
  for (let r = -3; r <= 3; r += 1) {
    const qStart = Math.max(-3, -r - 3);
    const qEnd = Math.min(3, -r + 3);
    for (let q = qStart; q <= qEnd; q += 1) coordinates.push({ q, r });
  }
  return coordinates;
})();

const axialIndex = new Map(axialCoordinates.map((coordinate, index) => [`${coordinate.q},${coordinate.r}`, index]));
const gravityOrder = axialCoordinates.map((coordinate, index) => ({ ...coordinate, index })).sort((first, second) => second.r - first.r);

export const HEXCHAIN_ADJACENCY = cellCoordinates.map((cell, index) => cellCoordinates
  .map((candidate, candidateIndex) => ({ candidateIndex, distance: Math.hypot(cell.x - candidate.x, cell.y - candidate.y) }))
  .filter(({ candidateIndex, distance }) => candidateIndex !== index && distance < 106)
  .map(({ candidateIndex }) => candidateIndex));

function nextRandom(state: number) {
  let value = state >>> 0 || 0x9e3779b9;
  value ^= value << 13;
  value ^= value >>> 17;
  value ^= value << 5;
  return value >>> 0;
}

function randomInt(player: HexchainPlayer, maximum: number) {
  player.rngState = nextRandom(player.rngState);
  return maximum > 0 ? player.rngState % maximum : 0;
}

function cloneBoard(board: HexchainTile[]) {
  return board.map((tile) => ({ ...tile }));
}

export function effectiveHexchainFreezeAge(_player: Pick<HexchainPlayer, "chains">) {
  return HEXCHAIN_FREEZE_AGE;
}

function syncFrozenTiles(player: HexchainPlayer) {
  const freezeAge = effectiveHexchainFreezeAge(player);
  player.board.forEach((tile) => {
    if (tile.special === "wild") {
      tile.age = 0;
      tile.frozen = false;
      return;
    }
    tile.frozen = tile.age >= freezeAge;
  });
}

function collapseAndRefill(player: HexchainPlayer, boardWithGaps: Array<HexchainTile | null>): HexchainBoardTransition {
  const working = boardWithGaps.map((tile, origin) => tile ? { tile: { ...tile }, origin } : null);
  let changed = true;
  let pass = 0;
  while (changed && pass < 10) {
    changed = false;
    for (const destination of gravityOrder) {
      if (working[destination.index]) continue;
      const upperLeft = axialIndex.get(`${destination.q},${destination.r - 1}`);
      const upperRight = axialIndex.get(`${destination.q + 1},${destination.r - 1}`);
      const candidates = [upperLeft, upperRight].filter((index): index is number => index !== undefined && Boolean(working[index]));
      if (!candidates.length) continue;
      const source = candidates.length === 1 ? candidates[0] : candidates[randomInt(player, candidates.length)];
      working[destination.index] = working[source];
      working[source] = null;
      changed = true;
    }
    pass += 1;
  }

  const spawnedIndexes: number[] = [];
  working.forEach((entry, index) => {
    if (entry) return;
    spawnedIndexes.push(index);
    working[index] = { tile: { color: randomInt(player, 6), age: 0, frozen: false }, origin: -1 };
  });
  player.movesSinceWildcard = Number.isFinite(player.movesSinceWildcard) ? player.movesSinceWildcard : 0;
  player.nextWildcardAt = Number.isFinite(player.nextWildcardAt) ? player.nextWildcardAt : 8;
  if (player.movesSinceWildcard >= player.nextWildcardAt && spawnedIndexes.length) {
    const wildcardIndex = spawnedIndexes[randomInt(player, spawnedIndexes.length)];
    working[wildcardIndex]!.tile.special = "wild";
    working[wildcardIndex]!.tile.age = 0;
    working[wildcardIndex]!.tile.frozen = false;
    player.movesSinceWildcard = 0;
    player.nextWildcardAt = 8 + randomInt(player, 3);
  }
  const board = working.map((entry) => ({ ...entry!.tile }));
  const moved = working.flatMap((entry, to) => entry && entry.origin >= 0 && entry.origin !== to ? [{ from: entry.origin, to }] : []);
  return { board, clearedIndexes: [], moved, spawnedIndexes };
}

function randomBoard(player: HexchainPlayer) {
  player.board = Array.from({ length: HEXCHAIN_TILE_COUNT }, () => ({ color: randomInt(player, 6), age: 0, frozen: false }));
  const seedColor = randomInt(player, 6);
  for (const index of [17, 18, 24]) player.board[index].color = seedColor;
}

export function createInitialHexchainState(): HexchainState {
  return {
    status: "lobby",
    durationMs: HEXCHAIN_MATCH_MS,
    startedAt: null,
    deadline: null,
    seed: 0,
    players: {},
    playerOrder: [],
    winnerIds: [],
    lastEvent: null,
    eventSequence: 0,
  };
}

export function publicHexchainState(state: HexchainState): HexchainPublicState {
  return {
    ...state,
    players: Object.fromEntries(Object.entries(state.players).map(([id, player]) => {
      const { rngState: _rngState, ...publicPlayer } = player;
      return [id, publicPlayer];
    })),
  };
}

export function startHexchain(state: HexchainState, roomPlayers: HexchainRoomPlayer[], now = Date.now(), seed = Math.floor(Math.random() * 0xffffffff)) {
  const contestants = roomPlayers.filter((player) => player.connected);
  if (state.status !== "lobby" || contestants.length < 1) return false;
  state.status = "battle";
  state.startedAt = now;
  state.deadline = contestants.length === 1 ? null : now + state.durationMs;
  state.seed = seed >>> 0 || 1;
  state.playerOrder = contestants.map((player) => player.id);
  state.players = {};
  state.winnerIds = [];
  state.lastEvent = null;
  state.eventSequence = 0;
  contestants.forEach((roomPlayer) => {
    const player: HexchainPlayer = {
      id: roomPlayer.id,
      name: roomPlayer.name,
      board: [],
      score: 0,
      chains: 0,
      bestChain: 0,
      fiveStreak: 0,
      incomingFrost: 0,
      shuffleLeft: true,
      alive: true,
      stuck: false,
      movesSinceWildcard: 0,
      nextWildcardAt: 8,
      rngState: state.seed,
    };
    randomBoard(player);
    state.players[player.id] = player;
  });
  return true;
}

export function isValidHexchainPath(player: HexchainPlayer, path: unknown): path is number[] {
  if (!Array.isArray(path) || path.length < 3 || path.length > HEXCHAIN_TILE_COUNT) return false;
  if (!path.every((index) => Number.isInteger(index) && index >= 0 && index < player.board.length)) return false;
  if (new Set(path).size !== path.length) return false;
  const color = path.map((index) => player.board[index]).find((tile) => tile?.special !== "wild")?.color;
  if (path.some((index) => {
    const tile = player.board[index];
    return !tile || tile.frozen || (color !== undefined && tile.special !== "wild" && tile.color !== color);
  })) return false;
  return path.slice(1).every((index, position) => HEXCHAIN_ADJACENCY[path[position]].includes(index));
}

export function hasHexchainMove(player: HexchainPlayer) {
  const canReachThree = (index: number, color: number, visited: Set<number>): boolean => {
    if (visited.size >= 3) return true;
    return HEXCHAIN_ADJACENCY[index].some((neighbor) => {
      const tile = player.board[neighbor];
      if (!tile || tile.frozen || (tile.special !== "wild" && tile.color !== color) || visited.has(neighbor)) return false;
      const next = new Set(visited);
      next.add(neighbor);
      return canReachThree(neighbor, color, next);
    });
  };
  return player.board.some((tile, index) => {
    if (tile.frozen) return false;
    const colors = tile.special === "wild"
      ? [...new Set(HEXCHAIN_ADJACENCY[index].map((neighbor) => player.board[neighbor]).filter((neighbor) => neighbor && !neighbor.frozen && neighbor.special !== "wild").map((neighbor) => neighbor.color))]
      : [tile.color];
    return colors.some((color) => canReachThree(index, color, new Set([index])));
  });
}

function attackPower(chainLength: number) {
  if (chainLength >= 7) return 4 + Math.floor((chainLength - 7) / 2);
  if (chainLength === 6) return 3;
  if (chainLength === 5) return 2;
  if (chainLength === 4) return 1;
  return 0;
}

function defensePower(chainLength: number) {
  return Math.max(0, chainLength - 3);
}

function landQueuedFrost(player: HexchainPlayer, amount: number) {
  const availableIndexes = player.board.flatMap((tile, index) => !tile.frozen && tile.special !== "wild" ? [index] : []);
  const freezeAge = effectiveHexchainFreezeAge(player);
  const landedIndexes: number[] = [];
  while (landedIndexes.length < amount && availableIndexes.length) {
    const choice = randomInt(player, availableIndexes.length);
    const [index] = availableIndexes.splice(choice, 1);
    const tile = player.board[index];
    tile.age = freezeAge;
    tile.frozen = true;
    landedIndexes.push(index);
  }
  return landedIndexes;
}

function chooseTarget(state: HexchainState, playerId: string, requestedTargetId?: unknown) {
  const eligible = state.playerOrder.map((id) => state.players[id]).filter((player) => player?.alive && player.id !== playerId);
  if (!eligible.length) return null;
  if (typeof requestedTargetId === "string") {
    const requested = eligible.find((player) => player.id === requestedTargetId);
    if (requested) return requested;
  }
  return [...eligible].sort((first, second) => second.score - first.score || first.id.localeCompare(second.id))[0];
}

function setEvent(state: HexchainState, event: Omit<HexchainEvent, "id">) {
  state.eventSequence += 1;
  state.lastEvent = { id: state.eventSequence, ...event };
}

function updatePlayerViability(state: HexchainState, player: HexchainPlayer) {
  if (!player.alive) return false;
  player.stuck = !hasHexchainMove(player);
  if (!player.stuck) return false;
  if (player.shuffleLeft) {
    // Preserve the chain event so its clear animation, score callout, and sound
    // reach the player. The board's stuck notice already directs them to Shuffle.
    return false;
  }
  player.alive = false;
  setEvent(state, { kind: "eliminated", playerId: player.id, message: `${player.name} is frozen out!` });
  return true;
}

function finishIfOneRemains(state: HexchainState) {
  const alive = state.playerOrder.filter((id) => state.players[id]?.alive);
  const solo = state.playerOrder.length === 1;
  if (solo ? alive.length === 1 : alive.length > 1) return false;
  const winnerIds = alive.length ? alive : highestScoreIds(state);
  finishHexchain(state, Date.now(), winnerIds);
  return true;
}

function highestScoreIds(state: HexchainState) {
  const contestants = state.playerOrder.map((id) => state.players[id]).filter(Boolean);
  const alive = contestants.filter((player) => player.alive);
  const eligible = alive.length ? alive : contestants;
  const highScore = eligible.length ? Math.max(...eligible.map((player) => player.score)) : 0;
  return eligible.filter((player) => player.score === highScore).map((player) => player.id);
}

export function playHexchain(state: HexchainState, playerId: string, path: unknown, requestedTargetId?: unknown, now = Date.now()) {
  if (state.status !== "battle") return false;
  if (state.deadline && now >= state.deadline) {
    finishHexchain(state, now);
    return true;
  }
  const player = state.players[playerId];
  if (!player?.alive || !isValidHexchainPath(player, path)) return false;

  const chain = path;
  const color = chain.map((index) => player.board[index]).find((tile) => tile.special !== "wild")?.color ?? player.board[chain[0]].color;
  const before = cloneBoard(player.board);
  const base = POINTS[chain.length] || Math.floor(10_000 * Math.pow(1.6, chain.length - 10));
  const previousBestChain = player.bestChain;
  player.fiveStreak = chain.length >= 5 ? player.fiveStreak + 1 : 0;
  const multiplier = Math.max(1, player.fiveStreak);
  const baseGained = base * multiplier;
  player.score += baseGained;
  player.chains += 1;
  player.movesSinceWildcard = (player.movesSinceWildcard || 0) + 1;
  player.bestChain = Math.max(player.bestChain, chain.length);

  const working: Array<HexchainTile | null> = cloneBoard(player.board);
  chain.forEach((index) => { working[index] = null; });
  const resetIndexes: number[] = [];
  let resetKind: HexchainAnimation["resetKind"];
  if (chain.length >= 7) {
    resetKind = "all";
    working.forEach((tile, index) => {
      if (!tile || tile.age <= 0) return;
      resetIndexes.push(index);
      tile.age = 0;
      tile.frozen = false;
    });
  } else if (chain.length >= 6) {
    resetKind = "color";
    working.forEach((tile, index) => {
      if (!tile || tile.special === "wild" || tile.color !== color || tile.age <= 0) return;
      resetIndexes.push(index);
      tile.age = 0;
      tile.frozen = false;
    });
  }

  const freezeAge = effectiveHexchainFreezeAge(player);
  working.forEach((tile) => {
    if (!tile || tile.special === "wild") return;
    tile.age = Math.min(HEXCHAIN_FREEZE_AGE, tile.age + 1);
    tile.frozen = tile.age >= freezeAge;
  });

  const primary = collapseAndRefill(player, working);
  primary.clearedIndexes = [...chain];
  player.board = cloneBoard(primary.board);

  let wipe: HexchainAnimation["wipe"];
  let wipeBonus = 0;
  if (chain.length >= 4) {
    const highlightedIndexes = player.board.flatMap((tile, index) => tile.special !== "wild" && tile.color === color ? [index] : []);
    if (highlightedIndexes.length) {
      const wipeWorking: Array<HexchainTile | null> = cloneBoard(player.board);
      highlightedIndexes.forEach((index) => { wipeWorking[index] = null; });
      const wipeTransition = collapseAndRefill(player, wipeWorking);
      wipeTransition.clearedIndexes = [...highlightedIndexes];
      wipe = { ...wipeTransition, highlightedIndexes };
      player.board = cloneBoard(wipe.board);
      wipeBonus = highlightedIndexes.length * 50;
      player.score += wipeBonus;
    }
  }

  const queuedFrost = player.incomingFrost;
  const cancelled = Math.min(queuedFrost, defensePower(chain.length));
  const uncounteredFrost = queuedFrost - cancelled;
  const beforeFrost = cloneBoard(player.board);
  const landedIndexes = landQueuedFrost(player, uncounteredFrost);
  const landed = landedIndexes.length;
  player.incomingFrost = uncounteredFrost - landed;
  syncFrozenTiles(player);
  const frost = landed ? { before: beforeFrost, after: cloneBoard(player.board), landedIndexes } : undefined;
  const gained = baseGained + wipeBonus;

  const generatedAttack = attackPower(chain.length);
  const target = generatedAttack ? chooseTarget(state, playerId, requestedTargetId) : null;
  let attack = 0;
  if (target) {
    target.incomingFrost += generatedAttack;
    attack = generatedAttack;
  }

  setEvent(state, {
    kind: "chain",
    playerId,
    ...(target ? { targetPlayerId: target.id } : {}),
    chainLength: chain.length,
    gained,
    baseGained,
    wipeBonus,
    previousBestChain,
    attack,
    cancelled,
    landed,
    path: [...chain],
    animation: { before, primary, ...(wipe ? { wipe } : {}), ...(frost ? { frost } : {}), chainColor: color, ...(resetKind ? { resetKind } : {}), resetIndexes },
    message: `${target
      ? `${player.name} linked ${chain.length}${chain.length >= 4 ? " and wiped the color" : ""}, then sent ${attack} frost to ${target.name}`
      : `${player.name} linked a ${chain.length}-chain${wipeBonus ? ` and earned +${wipeBonus} color-wipe bonus` : ""}`}${queuedFrost
      ? ` · ${cancelled} countered, ${landed} landed${player.incomingFrost ? `, ${player.incomingFrost} still incoming` : ""}`
      : ""}!`,
  });
  updatePlayerViability(state, player);
  finishIfOneRemains(state);
  return true;
}

export function shuffleHexchain(state: HexchainState, playerId: string, now = Date.now()) {
  if (state.status !== "battle") return false;
  if (state.deadline && now >= state.deadline) {
    finishHexchain(state, now);
    return true;
  }
  const player = state.players[playerId];
  if (!player?.alive || !player.shuffleLeft) return false;
  const before = cloneBoard(player.board);
  player.shuffleLeft = false;
  player.incomingFrost = 0;
  player.fiveStreak = 0;
  const resetIndexes: number[] = [];
  const tagged = player.board.map((tile, origin) => ({ tile: { ...tile }, origin }));
  const movableIndexes = tagged.flatMap(({ tile }, index) => tile.frozen ? [] : [index]);
  let shuffled = [...tagged];
  for (let attempt = 0; attempt < 80; attempt += 1) {
    shuffled = [...tagged];
    const movableTiles = movableIndexes.map((index) => tagged[index]);
    for (let index = movableTiles.length - 1; index > 0; index -= 1) {
      const swapIndex = randomInt(player, index + 1);
      [movableTiles[index], movableTiles[swapIndex]] = [movableTiles[swapIndex], movableTiles[index]];
    }
    movableIndexes.forEach((destination, index) => { shuffled[destination] = movableTiles[index]; });
    player.board = shuffled.map(({ tile }) => ({ ...tile }));
    if (hasHexchainMove(player)) break;
  }
  if (!hasHexchainMove(player)) {
    const colorCounts = new Map<number, number>();
    shuffled.forEach(({ tile }) => { if (!tile.frozen && tile.special !== "wild") colorCounts.set(tile.color, (colorCounts.get(tile.color) || 0) + 1); });
    const seedColor = [...colorCounts].filter(([, count]) => count >= 3).sort((first, second) => second[1] - first[1])[0]?.[0];
    const movableSet = new Set(movableIndexes);
    let targetPath: number[] | undefined;
    for (const first of movableIndexes) {
      for (const second of HEXCHAIN_ADJACENCY[first].filter((index) => movableSet.has(index))) {
        const third = HEXCHAIN_ADJACENCY[second].find((index) => index !== first && movableSet.has(index));
        if (third !== undefined) { targetPath = [first, second, third]; break; }
      }
      if (targetPath) break;
    }
    if (seedColor !== undefined && targetPath) {
      const fixedTargets = new Set<number>();
      targetPath.forEach((targetIndex) => {
        const targetTile = shuffled[targetIndex].tile;
        if (targetTile.special === "wild" || targetTile.color === seedColor) {
          fixedTargets.add(targetIndex);
          return;
        }
        const sourceIndex = movableIndexes.find((index) => !fixedTargets.has(index) && shuffled[index].tile.special !== "wild" && shuffled[index].tile.color === seedColor);
        if (sourceIndex !== undefined) [shuffled[targetIndex], shuffled[sourceIndex]] = [shuffled[sourceIndex], shuffled[targetIndex]];
        fixedTargets.add(targetIndex);
      });
    }
    player.board = shuffled.map(({ tile }) => ({ ...tile }));
  }
  syncFrozenTiles(player);
  player.stuck = !hasHexchainMove(player);
  const moved = shuffled.flatMap((entry, to) => entry.origin !== to ? [{ from: entry.origin, to }] : []);
  const frozenCount = player.board.filter((tile) => tile.frozen).length;
  setEvent(state, {
    kind: "shuffle",
    playerId,
    animation: { before, primary: { board: cloneBoard(player.board), clearedIndexes: [], moved, spawnedIndexes: [] }, resetKind: "shuffle", resetIndexes },
    message: `${player.name} shuffled the unfrozen tiles${frozenCount ? ` while ${frozenCount} frozen ${frozenCount === 1 ? "tile stayed" : "tiles stayed"} locked` : ""}!`,
  });
  if (player.stuck) {
    player.alive = false;
    setEvent(state, { kind: "eliminated", playerId: player.id, message: `${player.name} could not uncover another chain and is frozen out!` });
    finishIfOneRemains(state);
  }
  return true;
}

export function finishHexchain(state: HexchainState, now = Date.now(), forcedWinnerIds?: string[]) {
  if (state.status !== "battle") return false;
  state.status = "finished";
  state.deadline = null;
  state.winnerIds = forcedWinnerIds?.length ? forcedWinnerIds : highestScoreIds(state);
  const names = state.winnerIds.map((id) => state.players[id]?.name).filter(Boolean).join(" & ");
  const soloPlayer = state.playerOrder.length === 1 ? state.players[state.playerOrder[0]] : null;
  setEvent(state, {
    kind: "finished",
    playerId: state.winnerIds[0] || "",
    message: soloPlayer
      ? `${soloPlayer.name}'s solo run ended at ${soloPlayer.score.toLocaleString()} points!`
      : `${names || "The room"} won the siege!`,
  });
  return true;
}
